import * as z from "zod";

const productTypeSchema = z.enum([
  "returns",
  "order-editing",
  "oms",
  "email-sms",
  "conversion",
  "support-ai",
]);

const onboardingTypeSchema = z.enum(["default", "custom"]);

const stepsConfigurationSchema = z.object({
  /** Step ID -> array of substep IDs to show */
  steps: z.record(z.string(), z.array(z.string())),
  /** Auto-push collected data to merchant account (defaults to false for returns, true for order-editing) */
  autoPush: z.boolean().optional().default(false),
  /** Skip the billing step entirely */
  skipBilling: z.boolean().optional(),
  /** Skip the entire coverage step */
  skipCoverage: z.boolean().optional(),
  /** Skip all pricing steps (industry-selection, coverage-price, confirmation) */
  skipPricing: z.boolean().optional(),
  /** If false, billing step is optional (can continue without filling). Default: true */
  billingRequired: z.boolean().optional(),
  /** Custom coverage price for returns in cents (e.g., 298 for $2.98) */
  customCoveragePrice: z.number().int().optional(),
  /** Onboarding type - default or custom */
  onboardingType: onboardingTypeSchema.optional(),
  /** Enable notes panel for merchants to add notes during onboarding */
  enableOnboardingQuestionsForSE: z.boolean().optional(),
});

const createdBySchema = z.object({
  userId: z.string().optional(),
  name: z.string(),
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  department: z.string().optional(),
});

const assigneeSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  department: z.string().optional(),
  name: z.string().optional(),
});

export const saveOnboardingConfiguration = {
  name: "saveOnboardingConfiguration" as const,
  input: z
    .object({
      /** Merchant Team ID - required for existing merchants, null for pre-install configs */
      merchantId: z.string().min(1).nullable().optional(),
      /** Public/custom domain for pre-install configs (e.g., coolstore.com) */
      publicDomain: z.string().min(1).optional(),
      merchantName: z.string().min(1),
      product: productTypeSchema,
      stepsConfig: stepsConfigurationSchema,
      createdBy: createdBySchema,
      notes: z.string().optional(),
      assignees: z.array(assigneeSchema).max(5).optional().default([]),
    })
    .refine((data) => data.merchantId || data.publicDomain, {
      message: "Either merchantId or publicDomain must be provided",
    }),
  output: z.object({
    success: z.boolean(),
    /** merchantId if provided, or null for pre-install configs */
    merchantId: z.string().nullable(),
    /** publicDomain if this is a pre-install config */
    publicDomain: z.string().optional(),
    product: productTypeSchema,
    isNew: z.boolean(),
  }),
};

export type Assignee = z.infer<typeof assigneeSchema>;
export type SaveOnboardingConfigurationInput = z.infer<
  typeof saveOnboardingConfiguration.input
>;
export type SaveOnboardingConfigurationOutput = z.infer<
  typeof saveOnboardingConfiguration.output
>;
